import {tx, ptx, usePageText} from '../lib/page-text';
import { useEffect, useState } from "react";
import type { BroadcastControl } from "../hooks/useBroadcastControl";
import { DashboardDialog } from "./DashboardDialog";

export function BroadcastNotice({
  control,
  onEnd,
}: {
  control: BroadcastControl;
  onEnd: () => void;
}) {
  usePageText();
  if (!control.canEdit) return null;
  if (control.query.isError)
    return (
      <div className="card" role="alert">{tx("Broadcast controls could not refresh.")}{" "}
        <button
          className="btn outline sm"
          onClick={() => void control.query.refetch()}
        >{tx("Reload status")}</button>
      </div>
    );
  if (!control.query.data?.blocked) return null;
  const stopping = control.query.data.stopping;
  return (
    <div className="card broadcast-notice" role="status">
      <div>
        <strong>
          {stopping ? tx("End stream needs verification") : tx("Broadcast ended")}
        </strong>
        <p className="muted">
          {stopping
            ? tx("New connections are blocked. Verify that the publisher disconnected before starting again.")
            : tx("New connections are paused. Allow a new broadcast when you are ready; your stream link stays the same.")}
        </p>
        {control.error && (
          <p role="alert" className="error">
            {control.error}
          </p>
        )}
      </div>
      <button
        className="btn outline"
        disabled={control.action.isPending}
        onClick={() => (stopping ? onEnd() : control.action.mutate("resume"))}
      >
        {control.action.isPending
          ? tx("Checking…")
          : stopping
            ? tx("Retry End stream")
            : tx("Allow new broadcast")}
      </button>
    </div>
  );
}

export function EndBroadcastDialog({
  control,
  onClose,
  onStopped,
}: {
  control: BroadcastControl;
  onClose: () => void;
  onStopped: () => void;
}) {
  usePageText();
  const [generation, setGeneration] = useState<string | null>(null);
  const refetch = control.query.refetch;
  const refresh = async () => { setGeneration(null); control.action.reset(); const result = await refetch(); if (!result.isError && result.data) setGeneration(result.data.generation); };
  useEffect(() => { let active = true; void refetch().then(result => { if (active && !result.isError && result.data) setGeneration(result.data.generation); }); return () => { active = false; }; }, [refetch]);
  return (
    <DashboardDialog
      title={tx("End this stream for everyone?")}
      onClose={onClose}
      busy={control.action.isPending}
    >
      <p>{tx("This disconnects the publisher, including broadcasts from another device or an encoder. New connections stay paused until you allow a new broadcast.")}</p>
      <p className="muted">{tx("Your stream, viewer link and existing recordings are kept.")}</p>
      {control.error && (
        <p className="error" role="alert">
          {control.error}
        </p>
      )}
      {(control.query.isError || control.action.isError) && (
        <p role="alert">{ptx("Refresh the broadcast status before trying again.{v0}{v1}", {v0: (" "), v1: (<button
            className="btn outline sm"
            onClick={() => void refresh()}
          >{tx("Reload status")}</button>)})}</p>
      )}
      <div className="actions">
        <button
          className="btn outline"
          disabled={control.action.isPending}
          onClick={onClose}
        >{tx("Keep streaming")}</button>
        <button
          className="btn danger"
          disabled={
            control.action.isPending ||
            !generation ||
            control.query.isError
          }
          onClick={() =>
            control.action.mutate({ kind: "stop", generation: generation! }, {
              onSuccess: () => {
                onStopped();
                onClose();
              },
            })
          }
        >
          {control.action.isPending ? tx("Ending & verifying…") : tx("End stream")}
        </button>
      </div>
    </DashboardDialog>
  );
}
